import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiResponseError, IApiState } from '../../../types/api';
import { IPendingTransactionForApproval } from '../../../types/pendingTransaction';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';

interface IGetPendingTransactionsForApprovalRequest {
  /** Initiator's id */
  initiator?: string;
  /** 'transfer' | 'bill-payment' */
  type?: string;
  minAmount?: string | number;
  maxAmount?: string | number;
  from?: string;
  to?: string;
}

interface IGetPendingTransactionsForApprovalResponse {
  status?: boolean;
  message?: string;
  data?: IPendingTransactionForApproval[];
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetPendingTransactionsForApprovalResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getPendingTransactionsForApprovalAsync = createAsyncThunk(
  'pendingTransactions/approval',
  async (
    {
      to = '',
      from = '',
      type = '',
      initiator = '',
      maxAmount,
      minAmount,
    }: IGetPendingTransactionsForApprovalRequest,
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ??
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.get(
        `/transaction/pending?to=${to}&from=${from}&type=${type}&initiator=${initiator}&maxAmount=${
          maxAmount !== '' && maxAmount ? Number(maxAmount) * 100 : ''
        }&minAmount=${
          minAmount !== '' && minAmount ? Number(minAmount) * 100 : ''
        }`,
        config
      );

      return data;
    } catch (err) {
      const error =
        err as AxiosError<IGetPendingTransactionsForApprovalResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
        customMessage:
          'An error occurred while trying to apply filters, try again',
      });

      dispatch(
        showAlert({
          status: 'error',
          description: errorMessage,
        })
      );

      return rejectWithValue(error?.response?.data);
    }
  }
);

export const pendingTransactionsGetSlice = createSlice({
  name: 'pendingTransactions/approval/get',
  initialState,
  reducers: {
    resetGetPendingTransactionsForApproval: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPendingTransactionsForApprovalAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(
        getPendingTransactionsForApprovalAsync.fulfilled,
        (state, action) => {
          state.status = 'success';
          state.value =
            action.payload as IGetPendingTransactionsForApprovalResponse;
        }
      )
      .addCase(
        getPendingTransactionsForApprovalAsync.rejected,
        (state, action) => {
          state.status = 'failed';
          state.value =
            action.payload as IGetPendingTransactionsForApprovalResponse;
          state.error = action.error.message;
        }
      );
  },
});

export const { resetGetPendingTransactionsForApproval } =
  pendingTransactionsGetSlice.actions;

export default pendingTransactionsGetSlice.reducer;
